import {
    Avatar,
    Badge,
    Box,
    Flex,
    Heading,
    SimpleGrid,
    Stack,
    Text
} from '@chakra-ui/react'
import { RegistryParent, getParentName } from '../types'
import { useParentGuardians } from '../hooks/useParentGuardians'
import RegistryChildCard from './RegistryChildCard'

type Props = { parent: RegistryParent | null; coParent?: RegistryParent | null }

const Field = ({ label, value }: { label: string; value?: string | null }) => (
    <Box>
        <Text fontSize="xs" textTransform="uppercase" color="gray.500">
            {label}
        </Text>
        <Text fontSize="sm">{value?.trim() ? value : '—'}</Text>
    </Box>
)

export default function ParentDetails({ parent, coParent }: Props) {
    // Fetch all of this parent's guardians in a single request instead of one
    // per child card. Called unconditionally (before the early return below)
    // to satisfy the rules of hooks.
    const { guardiansByChildId } = useParentGuardians(parent?._id)

    if (!parent) {
        return (
            <Flex justify="center" align="center" h="full" minH="240px" p={6}>
                <Text color="gray.500">Select a parent to view details.</Text>
            </Flex>
        )
    }

    const children = parent.children ?? []
    const address = [parent.address, parent.city, parent.state, parent.country]
        .filter((value) => value && value.trim())
        .join(', ')
    const coParentName = coParent ? getParentName(coParent) : ''

    return (
        <Box p={{ base: 4, md: 5 }}>
            <Flex justify="space-between" align="start" flexWrap="wrap" gap={4}>
                <Box flex="1" minW={{ base: 'full', md: '320px' }}>
                    <Heading size="md">{getParentName(parent)}</Heading>
                    <Text color="gray.600" fontSize="sm">
                        {parent.relationship || 'Parent'}
                    </Text>
                    <SimpleGrid
                        columns={{ base: 1, sm: 2 }}
                        spacingX={6}
                        spacingY={3}
                        mt={4}
                        maxW="xl"
                    >
                        <Field label="Email" value={parent.email} />
                        <Field label="Phone" value={parent.phoneNumber} />
                        <Field label="Branch" value={parent.branch?.name} />
                        <Field label="Address" value={address} />
                    </SimpleGrid>
                </Box>
                <Stack align={{ base: 'start', sm: 'end' }} spacing={2}>
                    <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
                        {children.length} {children.length === 1 ? 'child' : 'children'}
                    </Badge>
                    {coParent ? (
                        <Flex
                            align="center"
                            gap={2}
                            bg="gray.50"
                            borderWidth="1px"
                            borderColor="gray.100"
                            borderRadius="md"
                            px={3}
                            py={2}
                            maxW="260px"
                        >
                            <Avatar
                                size="sm"
                                name={coParentName}
                                src={coParent.picture ?? undefined}
                                flexShrink={0}
                            />
                            <Box minW={0}>
                                <Text
                                    fontSize="xs"
                                    textTransform="uppercase"
                                    color="gray.500"
                                    letterSpacing="wide"
                                >
                                    Co-Parent / Spouse
                                </Text>
                                <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                                    {coParentName}
                                </Text>
                                {coParent.phoneNumber ? (
                                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                        {coParent.phoneNumber}
                                    </Text>
                                ) : null}
                            </Box>
                        </Flex>
                    ) : null}
                </Stack>
            </Flex>

            <Box mt={5}>
                <Heading size="sm" mb={3}>
                    Children
                </Heading>
                {children.length === 0 ? (
                    <Box
                        borderWidth="1px"
                        borderStyle="dashed"
                        borderColor="gray.200"
                        borderRadius="md"
                        p={6}
                        textAlign="center"
                    >
                        <Text color="gray.500" fontSize="sm">
                            No children linked to this parent.
                        </Text>
                    </Box>
                ) : (
                    <Stack spacing={3}>
                        {children.map((child) => (
                            <RegistryChildCard
                                key={child._id}
                                child={child}
                                guardians={guardiansByChildId(child._id)}
                            />
                        ))}
                    </Stack>
                )}
            </Box>
        </Box>
    )
}
